import type { CaseSummary, TreeNodeResponse } from './cors-provenance-api';
import { fetchProvenanceTreePage } from './cors-provenance-api';
import { getCaseInternalName } from './functions';

/** Subfolder under each case where manual uploads are stored. */
export const MANUAL_FOLDER_NAME = 'Manual';

/** Shown above the new-case name field in Case File Management. */
export const NEW_CASE_NAME_SPEC =
  'Case names may only contain letters, numbers, and spaces. Names cannot begin with __.';

/** Shown above the new-folder name field in Case File Management. */
export const NEW_FOLDER_NAME_SPEC =
  'Folder names cannot contain / or \\, and cannot include .. path segments.';

/** Provenance list lag after a Manual upload or folder create. */
export const CASE_FILE_UPLOAD_PROCESSING_MESSAGE =
  'Files and folders will not appear in the list until they are processed. This may take several minutes.';

export function normalizeNewCaseNameInput(name: string): string {
  return name.trim();
}

/** Validate a user-entered case name for Case File Management create-case flow. */
export function validateNewCaseName(
  name: string,
  existingCases: readonly CaseSummary[],
): string | null {
  const trimmed = normalizeNewCaseNameInput(name);
  if (!trimmed) return 'Case name is required.';
  if (trimmed.startsWith('__')) return 'Case names cannot begin with __.';
  if (!/^[a-zA-Z0-9 ]+$/.test(trimmed)) {
    return 'Case names may only contain letters, numbers, and spaces.';
  }

  const lower = trimmed.toLowerCase();
  for (const entry of existingCases) {
    const candidates = [entry.caseName, getCaseInternalName(entry.caseName)].filter(Boolean);
    if (candidates.some((candidate) => candidate.trim().toLowerCase() === lower)) {
      return `A case named "${entry.caseName}" already exists.`;
    }
  }

  return null;
}

const MAX_MANUAL_UPLOAD_FILE_NAME_LEN = 512;
const MAX_MANUAL_FOLDER_NAME_LEN = 255;

function childNameTaken(name: string, existingNames: ReadonlySet<string>): boolean {
  const lower = name.toLowerCase();
  for (const existing of existingNames) {
    if (existing.toLowerCase() === lower) return true;
  }
  return false;
}

/** Validate a user-entered folder name for Case File Management create-folder flow. */
export function validateNewFolderName(
  name: string,
  existingChildNames: ReadonlySet<string>,
): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Folder name is required.';
  if (trimmed.length > MAX_MANUAL_FOLDER_NAME_LEN) return 'Folder name is too long.';
  if (trimmed.includes('/') || trimmed.includes('\\') || trimmed.includes('..')) {
    return 'Folder name cannot contain invalid path segments.';
  }
  if (childNameTaken(trimmed, existingChildNames)) {
    return `A file or folder named "${trimmed}" already exists in this folder.`;
  }
  return null;
}

function normalizePathSegments(path: string): string[] {
  return path
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

/** Provenance tree path for a case's Manual folder root. */
export function caseManualTreePath(caseName: string): string {
  return `${getCaseInternalName(caseName)}/${MANUAL_FOLDER_NAME}`;
}

/**
 * Provenance tree path to ``Manual/`` for a case.
 * Prefer ``caseRootPath`` from the provenance tree (often the case tag); fall back to
 * {@link getCaseInternalName} when the browse root is not yet known.
 */
export function resolveManualTreePath(caseName: string, caseRootPath?: string | null): string {
  const root = caseRootPath?.trim();
  if (root) {
    return `${root.replace(/\/+$/, '')}/${MANUAL_FOLDER_NAME}`;
  }
  return caseManualTreePath(caseName);
}

/**
 * Relative path under ``Manual/`` for uploads into ``folderTreePath``.
 * Returns ``""`` when the folder is the Manual root.
 */
export function manualUploadPrefixForFolder(
  folderTreePath: string,
  caseName: string,
  caseRootPath?: string | null,
): string {
  const folderSegments = normalizePathSegments(folderTreePath);
  const manualSegments = normalizePathSegments(resolveManualTreePath(caseName, caseRootPath));
  if (folderSegments.length < manualSegments.length) return '';
  for (let i = 0; i < manualSegments.length; i++) {
    if (folderSegments[i]?.toLowerCase() !== manualSegments[i]?.toLowerCase()) {
      return '';
    }
  }
  return folderSegments.slice(manualSegments.length).join('/');
}

/** Join a Manual-relative prefix with a basename for edge-lambda ``fileName``. */
export function manualUploadFileName(prefix: string, basename: string): string {
  const trimmedBasename = basename.trim();
  const trimmedPrefix = prefix.trim().replace(/^\/+|\/+$/g, '');
  if (!trimmedPrefix) return trimmedBasename;
  return `${trimmedPrefix}/${trimmedBasename}`;
}

/** True when ``folderTreePath`` is the case Manual folder or a subdirectory of it. */
export function isManualUploadFolder(
  folderTreePath: string,
  caseName: string | undefined | null,
  caseRootPath?: string | null,
): boolean {
  if (!caseName) return false;
  const manualRoot = resolveManualTreePath(caseName, caseRootPath);
  const folderSegments = normalizePathSegments(folderTreePath).map((s) => s.toLowerCase());
  const manualSegments = normalizePathSegments(manualRoot).map((s) => s.toLowerCase());
  if (folderSegments.length < manualSegments.length) return false;
  for (let i = 0; i < manualSegments.length; i++) {
    if (folderSegments[i] !== manualSegments[i]) return false;
  }
  return true;
}

/** True when a tree file row lives under ``…/<case>/Manual/…``. */
export function isManualUploadTreeFile(
  node: TreeNodeResponse,
  caseName: string | undefined | null,
  caseRootPath?: string | null,
): boolean {
  if (!caseName || node.nodeType === 'directory') return false;
  const parentPath =
    normalizePathSegments(node.path).length > 1
      ? normalizePathSegments(node.path).slice(0, -1).join('/')
      : node.path;
  return isManualUploadFolder(parentPath, caseName, caseRootPath);
}

/** Validate a manual upload name relative to ``Manual/`` (may include subfolders). */
export function validateManualUploadFileName(
  fileName: string,
  existingNames: ReadonlySet<string>,
): string | null {
  const name = fileName.trim();
  if (!name) return 'File name is required.';
  if (name.length > MAX_MANUAL_UPLOAD_FILE_NAME_LEN) return 'File name is too long.';
  if (name.includes('\\') || name.includes('..')) {
    return 'File name cannot contain invalid path segments.';
  }
  const base = name.split('/').pop()?.trim() ?? '';
  if (!base || base.includes('..')) return 'File name is required.';
  if (existingNames.has(name)) {
    return `A file named "${base}" already exists in this folder.`;
  }
  return null;
}

export function validateManualUploadRename(
  newFileName: string,
  conflictingFileName: string,
  existingNames: ReadonlySet<string>,
): string | null {
  const trimmed = newFileName.trim();
  const conflicting = conflictingFileName.trim();
  if (trimmed === conflicting) {
    return `Choose a different name than "${conflicting.split('/').pop()}".`;
  }
  const taken = new Set(existingNames);
  taken.add(conflicting);
  return validateManualUploadFileName(trimmed, taken);
}

/** Basename segment of a Manual-relative upload path. */
export function manualUploadBasename(fileName: string): string {
  const trimmed = fileName.trim();
  const slash = trimmed.lastIndexOf('/');
  return slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
}

/** Validate a rename entered as a basename within ``uploadPrefix``. */
export function validateManualUploadRenameBasename(
  newBasename: string,
  uploadPrefix: string,
  conflictingFileName: string,
  existingNames: ReadonlySet<string>,
): string | null {
  return validateManualUploadRename(
    manualUploadFileName(uploadPrefix, newBasename),
    conflictingFileName,
    existingNames,
  );
}

export function suggestManualUploadRenameBasename(
  fileName: string,
  existingNames: ReadonlySet<string>,
): string {
  return manualUploadBasename(suggestManualUploadRename(fileName, existingNames));
}

export function suggestManualUploadRename(
  fileName: string,
  existingNames: ReadonlySet<string>,
): string {
  const trimmed = fileName.trim();
  const slash = trimmed.lastIndexOf('/');
  const prefix = slash >= 0 ? trimmed.slice(0, slash + 1) : '';
  const base = slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot) : '';

  for (let i = 1; i < 100; i++) {
    const candidate = `${prefix}${stem} (${i})${ext}`;
    if (!existingNames.has(candidate)) return candidate;
  }
  return `${prefix}${stem} (${Date.now()})${ext}`;
}

/** List Manual-relative names currently stored under ``folderTreePath``. */
export async function fetchManualFolderFileNames(args: {
  cellId: string;
  accessToken: string;
  caseName: string;
  folderTreePath: string;
  caseRootPath?: string | null;
  signal?: AbortSignal;
}): Promise<Set<string>> {
  const names = new Set<string>();
  const prefix = manualUploadPrefixForFolder(
    args.folderTreePath,
    args.caseName,
    args.caseRootPath,
  );
  let offset = 0;
  const limit = 200;

  while (true) {
    const page = await fetchProvenanceTreePage({
      cellId: args.cellId,
      accessToken: args.accessToken,
      path: args.folderTreePath,
      view: 'user',
      offset,
      limit,
      signal: args.signal,
    });

    for (const node of page.items) {
      if (node.nodeType !== 'file') continue;
      const basename = node.name.trim();
      if (!basename) continue;
      names.add(manualUploadFileName(prefix, basename));
    }

    if (page.nextOffset === null) break;
    offset = page.nextOffset;
  }

  return names;
}

/** Basenames of files and directories currently listed under ``folderTreePath``. */
export async function fetchManualFolderChildBasenames(args: {
  cellId: string;
  accessToken: string;
  folderTreePath: string;
  signal?: AbortSignal;
}): Promise<Set<string>> {
  const names = new Set<string>();
  let offset = 0;
  const limit = 200;

  while (true) {
    const page = await fetchProvenanceTreePage({
      cellId: args.cellId,
      accessToken: args.accessToken,
      path: args.folderTreePath,
      view: 'user',
      offset,
      limit,
      signal: args.signal,
    });

    for (const node of page.items) {
      const basename = node.name.trim();
      if (basename) names.add(basename);
    }

    if (page.nextOffset === null) break;
    offset = page.nextOffset;
  }

  return names;
}
